import { paperOf, type PaperSize as SheetPaper } from '@core/print/sheetHtml';

/**
 * The three sizes a shop can print a booking document on, and what each one changes.
 *
 * <p>
 * <strong>They are three different media, not three widths.</strong> An 80mm roll goes through a
 * thermal head that burns a dot or does not — there is no greyscale, so every muted grey has to
 * become black or it dithers into a stipple a 9px glyph cannot survive; the paper is continuous, so
 * length is nearly free; and the type is monospaced because that is what the trade's receipts look
 * like and what prints crisply at 203dpi. A5 and A4 go through a laser or inkjet, where greys are
 * real, proportional type is better, and the page has a fixed bottom edge that content has to fit
 * above.
 * </p>
 *
 * <p>
 * <strong>All three exist because all three are somebody's counter.</strong> A shop with a thermal
 * roll beside the till, an office with an A4 printer, and the common middle case of A5 on a
 * half-sheet. Picking one for everybody would be picking wrong for two of them.
 * </p>
 */

export const PaperSizeValues = ['Thermal80', 'A5', 'A4', 'A4Landscape'] as const;

export type PaperSize = (typeof PaperSizeValues)[number];

export interface PaperSpec {
  id: PaperSize;
  /** What the picker calls it. */
  label: string;
  /** The one-line difference, for the picker's second line. */
  hint: string;
  /** Sheet width, as CSS. Continuous roll has no height. */
  width: string;
  /** The `@page size` declaration this size needs. */
  pageSize: string;
  /** The `@page margin`. A thermal roll has none — the head starts where the paper starts. */
  pageMargin: string;
  /**
   * True for a thermal head: no greyscale, monospaced type, unlimited length.
   *
   * The one flag the documents branch on, so a fourth size added later declares its nature rather
   * than being special-cased in every component.
   */
  thermal: boolean;
}

const specs: Record<PaperSize, PaperSpec> = {
  Thermal80: {
    id: 'Thermal80',
    label: '80 mm roll',
    hint: 'Thermal counter printer',
    // 80mm of paper at 96dpi. The 4mm of margin each side is padding inside the document, not a
    // page margin, because a thermal driver ignores page margins entirely.
    width: '302px',
    pageSize: '80mm auto',
    pageMargin: '0',
    thermal: true,
  },
  A5: {
    id: 'A5',
    label: 'A5',
    hint: 'Half sheet · 148 × 210 mm',
    width: '148mm',
    pageSize: 'A5',
    pageMargin: '10mm',
    thermal: false,
  },
  A4: {
    id: 'A4',
    label: 'A4',
    hint: 'Full sheet · 210 × 297 mm',
    width: '210mm',
    pageSize: 'A4',
    pageMargin: '12mm',
    thermal: false,
  },
  // Reports only (`reportPapers`): a register a dozen columns wide reads across the long edge.
  A4Landscape: {
    id: 'A4Landscape',
    label: 'A4 landscape',
    hint: 'Full sheet on its side · 297 × 210 mm',
    width: '297mm',
    pageSize: 'A4 landscape',
    pageMargin: '12mm',
    thermal: false,
  },
};

export function paperSpec(size: PaperSize): PaperSpec {
  return specs[size];
}

/**
 * The classes a document's root carries for this paper.
 *
 * <p>
 * `.doc` supplies the ink tokens every document colours itself from, and the second class overrides
 * them — `.doc-thermal` collapses every one to black and switches to monospaced type, because a
 * thermal head has no greyscale. Composed here rather than in each document, so a fourth paper is
 * one entry in this file and no edit at all in the components.
 * </p>
 */
export function documentClass(size: PaperSize): string {
  const variant =
    size === 'Thermal80'
      ? 'doc-thermal'
      : size === 'A4' || size === 'A4Landscape'
        ? 'doc-sheet doc-a4'
        : 'doc-sheet';

  return `doc ${variant}`;
}

export const allPaperSizes: readonly PaperSpec[] = PaperSizeValues.map((size) => specs[size]);

/**
 * The `@page` rule for a chosen size.
 *
 * <p>
 * Injected as a stylesheet rather than set through a class, because <strong>`@page` cannot be
 * scoped</strong> — it is a document-level at-rule, so there is no selector that makes one apply
 * and another not. A component that offers a choice of paper has to write the rule out.
 * </p>
 */
export function pageRule(size: PaperSize): string {
  const spec = specs[size];

  return `@page { size: ${spec.pageSize}; margin: ${spec.pageMargin}; }`;
}

/**
 * What the shop last chose, so a counter with one printer is not asked every time.
 *
 * <p>
 * `localStorage` rather than a tenant setting, deliberately: the paper is a property of the machine
 * in front of this person, not of the company. A chain whose head office prints A4 and whose
 * Kathiawadi branch has a thermal roll would be wrong under one shared value, and the branch that
 * lost the argument would re-pick on every print.
 * </p>
 */
const storageKey = 'attirerent.print.paper';

/**
 * The sizes a document offers.
 *
 * <p>
 * <strong>The tailor's docket is not an A4 document.</strong> It is a working sheet that travels
 * with a garment to a workshop and comes back marked up — on a roll beside the till, or on a half
 * sheet. Offering A4 there is offering to waste three quarters of a page on every job, so the
 * docket declares its own set rather than taking whatever the picker happens to list.
 * </p>
 */
export const docketPapers: readonly PaperSize[] = ['Thermal80', 'A5'];

/**
 * What a document handed over the counter offers when it names no set of its own: the roll, the
 * half sheet and the full sheet. A4 landscape is left out — it is a report's paper.
 */
export const documentPapers: readonly PaperSize[] = ['Thermal80', 'A5', 'A4'];

/**
 * <strong>Every report prints on A4 or A4 landscape, nothing else</strong> (owner, 2026-10-29). A
 * report is read at a desk and filed, never handed to a customer, so a roll or a half sheet has no
 * use for one; landscape is there for the registers whose columns outgrow the short edge.
 */
export const reportPapers: readonly PaperSize[] = ['A4', 'A4Landscape'];

/**
 * The most columns an A4 sheet stood upright carries at A4's type before figures crowd their
 * headings: a label column and seven counts (report A1) fit; a ninth column does not.
 */
export const portraitColumns = 8;

/**
 * <strong>Which way up a report prints is its layout's to say</strong> (owner, 2026-10-29: "depends
 * on print details size & layout"): upright while its widest table fits, on its side once it does
 * not. The clerk can still turn it for one print; nothing is remembered.
 */
export function reportPaperFor(columns: number): PaperSize {
  return columns > portraitColumns ? 'A4Landscape' : 'A4';
}

/**
 * The shop's own starting paper (`Print.DefaultPaper`, owner, 2026-10-15), set from the session in
 * `App.tsx`. A roll maps to the 80 mm the website lays out; the phone also offers 58 mm.
 */
let shopPaper: PaperSize | null = null;

// The same setting in the four papers the shared sheets take (`@core/print/sheetHtml`) — what the
// phone reads, and what every print built from those sheets starts on (2026-10-22).
let shopSheetPaper: SheetPaper = 'A5';

/** The shop's paper for a shared sheet, if this document offers it; else the fallback. */
export function shopSheetPaperOr(fallback: SheetPaper, allowed: readonly SheetPaper[]): SheetPaper {
  return allowed.includes(shopSheetPaper) ? shopSheetPaper : fallback;
}

export function setShopPaper(value: string | null | undefined): void {
  shopSheetPaper = paperOf(value);
  shopPaper =
    value === '58mm' || value === '80mm' ? 'Thermal80' : isPaperSize(value) ? value : null;
}

/** Where a document starts when this machine has no habit of its own: the shop's paper, if offered. */
export function shopPaperOr(fallback: PaperSize, allowed: readonly PaperSize[]): PaperSize {
  return shopPaper !== null && allowed.includes(shopPaper) ? shopPaper : fallback;
}

export function rememberedPaper(
  fallback: PaperSize = 'A5',
  key: string = storageKey,
  allowed: readonly PaperSize[] = PaperSizeValues,
): PaperSize {
  try {
    const stored = globalThis.localStorage?.getItem(key);

    // Clamped to what this document offers. A remembered A4 must not open a docket on a size its
    // own picker does not list — the buttons would then agree with nothing, and the first click
    // would look like it changed something that was already true.
    return isPaperSize(stored) && allowed.includes(stored) ? stored : fallback;
  } catch {
    // Private browsing and locked-down kiosks both throw here. A default is a fine answer.
    return fallback;
  }
}

export function rememberPaper(size: PaperSize, key: string = storageKey): void {
  try {
    globalThis.localStorage?.setItem(key, size);
  } catch {
    // Not being able to remember the choice is not a reason to fail the print.
  }
}

export function isPaperSize(value: unknown): value is PaperSize {
  return typeof value === 'string' && (PaperSizeValues as readonly string[]).includes(value);
}
